import { fetchBanners, fetchListBanners } from 'actions/ContentActions'
import * as Analytics from 'analytics/analytics'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import TourUSPContainer from 'components/OfferPage/TourV2/TourUSPContainer'
import TourV2TrendingDestinationsCarousel from 'components/OfferPage/TourV2/TourV2TrendingDestinationsCarousel'
import { getTourV2TrendingDestinations } from 'components/OfferPage/TourV2/TourV2TrendingLocationsList'
import TourSearchContainerPane, {
  TourSearchContainerPaneContent,
} from 'components/Search/TourV2/TourSearchContainerPane'
import TourSearchHeroContainer, { TourHeroImageGravityType } from 'components/Search/TourV2/TourSearchHeroContainer'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import TourPageCategoriesCarousel from 'components/Tours/TourPageCategoriesCarousel'
import TourPageSearchCarousel from 'components/Tours/TourPageSearchCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { TOUR_ONLY_REGIONS } from 'constants/config/region'
import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GeoContext from 'contexts/geoContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import { EmptyArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import styled from 'styled-components'
import ToursPageHighIntentTours from './ToursPageHighIntentTours'
import TourSpendEarnPageBanner from 'components/Tours/TourSpendEarnPageBanner'
import { isTourPromoSpendEarnEnabled } from 'selectors/TourV2/toursSelectors'
import TourCatalogueBanner from 'components/Tours/TourCatalogueBanner'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import TourHeroOfferCarousel from 'components/OfferList/OfferListTiles/TourHeroOfferCarousel'
import { getMonthlyTourHomepageHeroImageId } from 'lib/tours/tourUtils'
import { BRAND_SIGNATURE_SERIES, TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS } from 'constants/tours'

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-bottom: ${rem(16)};
  padding-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(48)};
  }
`

const LayoutContainerSearchBarMobile = styled(LayoutContainer)`
  position: absolute;
  bottom: 0;
  transform:translateY(50%);
`

interface MappedStateProps {
  banners: Array<App.Banner>
  listBanners: Array<App.ListBanner>
  urlFilters: App.OfferListFilters
  toursVerticalName: string,
  standaloneCruiseEnabled: boolean,
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

const toursDestinations = ['Egypt', 'New York', 'Africa', 'Europe']

interface CarouselDefinition {
  code: string;
  title: React.ReactNode;
  subtitle?: string;
  locations?: Array<App.Place>;
  operators?: Array<string>;
}

const exclusiveCarousel: CarouselDefinition = {
  code: 'luxexclusive',
  title: <>Today's top <i>exclusive</i> offers</>,
  locations: undefined,
}

const limitedTimeExclusiveCarousel: CarouselDefinition = {
  code: 'limited_time_exclusive',
  title: <>Explore our <i>limited time exclusive</i> tours</>,
  locations: undefined,
}

const tourStylesCarousels: Array<CarouselDefinition> = [
  {
    code: 'Deluxe',
    title: <><i>Deluxe</i> tours</>,
    subtitle: 'Discover the highlights and enjoy 4 & 4.5 star hotels with a maximum 24 travellers',
    locations: undefined,
  },
  {
    code: 'Premium',
    title: <><i>Premium</i> tours</>,
    subtitle: 'Elevated experiences with 5 star hotels and a maximum 16 travellers',
    locations: undefined,
  },
  {
    code: 'Ultralux',
    title: <><i>Ultra Lux</i> tours</>,
    subtitle: 'Only the best 5 star hotels & iconic stays with a maximum 12 travellers',
    locations: undefined,
  },
]

const privateJourneyCarousel: CarouselDefinition = {
  code: 'privatetours',
  title: <><i>Private Journeys</i> tours</>,
  subtitle: 'Intimate journeys with just you and your partner',
  locations: undefined,
}
const limitedTimeSpecialCarousel: CarouselDefinition = {
  code: 'limited_time_special',
  title: <><i>Limited-time</i> specials</>,
  locations: undefined,
}

const ttcOperatedCurrencies = new Set(['AUD', 'NZD', 'GBP', 'USD', 'CAD', 'EUR'])

function ToursPage(props: Props & MappedStateProps) {
  const {
    urlFilters,
    toursVerticalName,
    standaloneCruiseEnabled,
  } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const isTourV2SearchEnabled = useTourV2SearchEnabled()
  const currentSiteTakeover = useCurrentSiteTakeover('tours')
  const promoSpendEarnEnabled = useAppSelector(state => isTourPromoSpendEarnEnabled(state))
  const showOperatorCarousel = ttcOperatedCurrencies.has(currentCurrency)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
    dispatch(fetchListBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return {
        ...urlFilters,
        destinations: TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
        operators: [BRAND_SIGNATURE_SERIES, LUXURY_ESCAPES],
        offerTypes: [OFFER_TYPE_TOUR_V2],
        sortBy: 'vertical',
        limit: 250,
      }
    }
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      const isTourOnlyRegion = TOUR_ONLY_REGIONS.includes(currentRegionCode)
      const sortDefault = isTourOnlyRegion ? 'vertical' : 'vertical-tour'
      return {
        ...urlFilters,
        offerTypes: isTourOnlyRegion ? [OFFER_TYPE_TOUR_V2] : [OFFER_TYPE_TOUR_V2, OFFER_TYPE_TOUR, OFFER_TYPE_CRUISE],
        sortBy: urlFilters.sortBy ?? sortDefault,
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled, currentRegionCode, currentSiteTakeover])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  const heroSearchBackgroundProps = useMemo<{ heroImageId?: string, title: React.ReactNode, subtitle: string, gravity: TourHeroImageGravityType, tabletAspectRatio?: string }>(() => {
    if (currentSiteTakeover) {
      return {
        heroImageId: currentSiteTakeover?.imageId,
        title: <><i>Tour</i> the world with us</>,
        subtitle: 'Wander more with curated small-group adventures to destinations across the globe.',
        gravity: 'center',
        tabletAspectRatio: '4:1',
        aspectRatio: '2:1',
      }
    }
    const heroImage = getMonthlyTourHomepageHeroImageId()
    return {
      heroImageId: heroImage.id,
      title: <><i>Tour</i> the world with us</>,
      subtitle: 'Wander more with curated small-group adventures to destinations across the globe.',
      gravity: heroImage.gravity,
      aspectRatio: '2:1',
    }
  }, [currentSiteTakeover])

  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()

  const onTourCarrouselAnalyticEvent = useCallback<OfferListEventHandler<{category: string}>>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.productImpression && additionalData) {
      Analytics.trackClientEvent({
        action: 'view',
        subject: `offerlist-tourspage-${additionalData.category}-carousel`,
        category: 'vertical_page',
        type: 'nonInteraction',
      })
    }
  }, [])

  const onTourTrendingDestinationsAnalyticEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.clientAction) {
      const { subject, action } = dispatchAction
      Analytics.trackClientEvent({
        action,
        subject: `tour_trending_destinations_${subject}`,
        category: 'vertical_page',
        type: 'interaction',
      })
    }
  }, [])

  // Keep track of offer ids show from carousel to avoid duplication
  const [tourIdsMainCarousel, setTourIdsMainCarousel] = useState<Array<string>>([])
  const [tourIdsLuxExclusive, setTourIdsLuxExclusive] = useState<Array<string>>([])
  const [tourIdsLimitedTimeExclusive, setTourIdsLimitedTimeExclusive] = useState<Array<string>>([])
  const [tourIdsUltraLux, setTourIdsUltraLux] = useState<Array<string>>([])
  const [tourIdsPremium, setTourIdsPremium] = useState<Array<string>>([])
  const [tourIdsDeluxe, setTourIdsDeluxe] = useState<Array<string>>([])
  const [tourIdsTimeLimitedSpecial, setTourIdsTimeLimitedSpecial] = useState<Array<string>>([])
  const tourStylesToSetTourIdsShown = {
    Ultralux: setTourIdsUltraLux,
    Premium: setTourIdsPremium,
    Deluxe: setTourIdsDeluxe,
  }
  const excludeIdsForTimeLimitedExclusiveCarousel = useMemo(() => [
    ...tourIdsMainCarousel,
    ...tourIdsLuxExclusive,
  ], [tourIdsMainCarousel, tourIdsLuxExclusive])

  const excludeIdsForTourStylesCarousel = useMemo(() => [
    ...excludeIdsForTimeLimitedExclusiveCarousel,
    ...tourIdsLimitedTimeExclusive,
  ], [excludeIdsForTimeLimitedExclusiveCarousel, tourIdsLimitedTimeExclusive])

  const excludeIdsForTimeSpecialCarousel = useMemo(() => [
    ...excludeIdsForTourStylesCarousel,
    ...tourIdsUltraLux,
    ...tourIdsPremium,
    ...tourIdsDeluxe,
  ], [excludeIdsForTourStylesCarousel, tourIdsUltraLux, tourIdsPremium, tourIdsDeluxe])

  const excludeIdsForPrivateJourneyCarousel = useMemo(() => [
    ...excludeIdsForTimeSpecialCarousel,
    ...tourIdsTimeLimitedSpecial,
  ], [excludeIdsForTimeSpecialCarousel, tourIdsTimeLimitedSpecial])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <div>
      <HeadData
        title={`${headTitle} - ${config.title} ${currentRegionCode}`}
        description={description}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        {promoSpendEarnEnabled && <TourSpendEarnPageBanner />}
        {isTourV2SearchEnabled && <TourSearchHeroContainer
          {...heroSearchBackgroundProps}
        >
          <TourSearchContainerPane>
            <TourSearchContainerPaneContent>
              <TourV2SearchControls />
            </TourSearchContainerPaneContent>
          </TourSearchContainerPane>
          <LayoutContainerSearchBarMobile>
            <GlobalMobileSearchHeroBar placeholder="Search tours in" placeholderTextList={toursDestinations} />
          </LayoutContainerSearchBarMobile>
        </TourSearchHeroContainer>}
        <>
          <OfferListEventsProvider
            onListEvent={onTourCarrouselAnalyticEvent}
            additionalData={{ category: 'topexclusive' }}
          >
            <TourHeroOfferCarousel filters={filters} onOffersShow={setTourIdsMainCarousel} />
          </OfferListEventsProvider>
          <OffersContainer>
            <Group direction="vertical" gap={32} tabletGap={40}>
              {config.TOUR_CATALOG_ENABLED && <LayoutContainer>
                <TourCatalogueBanner />
              </LayoutContainer>}
              <ErrorBoundary fallback={null}>
                <ToursPageHighIntentTours />
              </ErrorBoundary>
              <Group direction="vertical" gap={56} tabletGap={80}>
                <OfferListEventsProvider onListEvent={onTourTrendingDestinationsAnalyticEvent}>
                  <TourV2TrendingDestinationsCarousel locations={getTourV2TrendingDestinations()} />
                </OfferListEventsProvider>
                <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData={{ category: 'topexclusive' }}
                >
                  {
                !!currentSiteTakeover &&
                  <TourPageSearchCarousel
                    offerIdsToExclude={tourIdsMainCarousel}
                    key={exclusiveCarousel.code}
                    title={exclusiveCarousel.title}
                    category={exclusiveCarousel.code}
                    places={exclusiveCarousel.locations}
                    sortType="vertical"
                    onOffersShow={setTourIdsLuxExclusive}
                  />
                }
                  <TourPageSearchCarousel
                    offerIdsToExclude={excludeIdsForTimeLimitedExclusiveCarousel}
                    key={limitedTimeExclusiveCarousel.code}
                    title={limitedTimeExclusiveCarousel.title}
                    places={limitedTimeExclusiveCarousel.locations}
                    sortType="vertical"
                    flashOffer
                    onOffersShow={setTourIdsLimitedTimeExclusive}
                  />
                </OfferListEventsProvider>
                <TourPageCategoriesCarousel />
                {config.BRAND === LUXURY_ESCAPES && showOperatorCarousel && <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData={{ category: 'topexclusive' }}
                >
                  <LayoutContainer>
                    <TourUSPContainer />
                  </LayoutContainer>
                </OfferListEventsProvider>}
                {tourStylesCarousels.map(carousel =>
                  <OfferListEventsProvider
                    onListEvent={onTourCarrouselAnalyticEvent}
                    additionalData={{ category: 'topexclusive' }}
                    key={carousel.code}
                  >
                    <TourPageSearchCarousel
                      offerIdsToExclude={excludeIdsForTourStylesCarousel}
                      title={carousel.title}
                      subtitle={carousel.subtitle}
                      style={carousel.code}
                      places={carousel.locations}
                      sortType="vertical"
                      onOffersShow={tourStylesToSetTourIdsShown[carousel.code]}
                    />
                  </OfferListEventsProvider>,
                )}
                <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData={{ category: 'topexclusive' }}
                >
                  <TourPageSearchCarousel
                    offerIdsToExclude={excludeIdsForTimeSpecialCarousel}
                    title={limitedTimeSpecialCarousel.title}
                    places={limitedTimeSpecialCarousel.locations}
                    onSale
                    sortType="vertical"
                    onOffersShow={setTourIdsTimeLimitedSpecial}
                  />
                </OfferListEventsProvider>

                <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData={{ category: 'topexclusive' }}
                >
                  <TourPageSearchCarousel
                    offerIdsToExclude={excludeIdsForPrivateJourneyCarousel}
                    title={privateJourneyCarousel.title}
                    subtitle={privateJourneyCarousel.subtitle}
                    places={privateJourneyCarousel.locations}
                    category={privateJourneyCarousel.code}
                    sortType="vertical"
                  />
                </OfferListEventsProvider>
              </Group>
            </Group>
          </OffersContainer>
        </>
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    listBanners: state.content.listBanners?.toursAndCruises ?? EmptyArray,
    banners: state.content.banners.toursAndCruises,
    urlFilters: getUrlOfferListFilters(state),
    toursVerticalName: tourV2VerticalNameSelector(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
